// Shared, dependency-free validation limits + the domain shape. ONE source (CLAUDE.md #3) for: the bot's Zod
// schema (schema.ts), the UI spec (spec.ts), the inline /config commands (bot/features/config), AND the Mini
// App frontend. It is PURE — no zod, no grammY — so the browser bundle can import it freely: the frontend must
// never value-import schema.ts (that drags zod in and trips the bundle-hygiene gate). Keeping the numbers here,
// not re-typed per layer, means a cap can't drift between the command path and the dashboard.

/** Max length of the welcome text (a single scalar string). Mirrored by welcomeCaptchaSchema.welcomeText. */
export const WELCOME_TEXT_MAX = 4096
/**
 * Max length of the group rules text (a single scalar string). Mirrored by rulesSchema.text. Capped at one
 * Telegram message (4096) because /rules sends it verbatim as a single message; a separate constant from
 * WELCOME_TEXT_MAX so the two caps can move independently.
 */
export const RULES_TEXT_MAX = 4096
/**
 * The ONE placeholder a custom welcome template may use: `{name}` → the joining member's first name
 * (substituted by bot/features/welcome-captcha/render.ts, case-insensitively). Kept here, dependency-free, so
 * the Mini App hint and the /setwelcome tip surface the SAME literal. render.ts's matcher is a separate regex
 * literal by necessity (a regex, not a string); render.test.ts asserts it stays in lockstep with this constant.
 */
export const WELCOME_NAME_TOKEN = '{name}'
/** Max length of one blocklist word. Mirrored by filtersSchema.blocklist's element. */
export const BLOCKLIST_ENTRY_MAX = 200
/** Max length of one allowlisted domain. Mirrored by filtersSchema.allowlistDomains's element. */
export const DOMAIN_ENTRY_MAX = 255
/** Max number of entries in either list (blocklist / allowlistDomains). Mirrored by the array .max(). */
export const LIST_MAX = 1000

// A pragmatic host shape: dot-separated lowercase labels (no scheme, no path), stored bare — exactly what
// detect.ts's isAllowlisted compares against. Lowercase-only on purpose: every write path lowercases first
// (the /allowdomain command, the schema transform, the Mini App parse), and matching is case-insensitive.
export const DOMAIN_RE = /^[a-z0-9-]+(?:\.[a-z0-9-]+)+$/

// Trusted-user entries are Telegram numeric user ids stored as strings. USERID_RE is the WRITE-path shape
// check (digits only — no @username, no sign); USERID_ENTRY_MAX bounds one id's length (Telegram ids fit well
// under 20 chars even with the announced 64-bit growth). Same one-source pattern as DOMAIN_RE — shared by the
// schema (write), the spec, the /trust command, and the Mini App parse, so the shape can't drift per layer.
export const USERID_ENTRY_MAX = 20
export const USERID_RE = /^\d+$/

// Night mode: a whole-hour nightly window during which the chat is read-only for non-admins, in the group's
// timezone. NIGHT_HOUR_MIN/MAX bound the start/end hours — shared by nightModeSchema, the spec int ladders
// (0–23), and the scheduler so the range can't drift per layer. TIMEZONE_MAX caps a stored IANA zone id (the
// longest real name is ~32 chars; 64 is safe headroom). The tz is validated FOR REAL on the write path via
// Intl (services/nightmode/time.ts isValidTimeZone) — a length cap is the only shape check that belongs in
// this pure, dependency-free module.
export const NIGHT_HOUR_MIN = 0
export const NIGHT_HOUR_MAX = 23
export const TIMEZONE_MAX = 64

// AI Shield: bounds on the per-group daily classify-call budget (settings.aiShieldBudget — the cost cap the
// dashboard exposes). Shared by the schema's write bounds and the spec's int ladder so they can't drift.
// Min 10 keeps the instrument meaningful (a lower cap measures nothing); max 2000 bounds the worst-case
// spend a single group can dial in (~10× the default 200).
export const AI_BUDGET_MIN = 10
export const AI_BUDGET_MAX = 2000
